import {
  DetailedEstimationData,
  CategoryData,
  CalculatedItem,
  CategoryTotal,
  EstimationResult,
  TVABreakdown,
  TVARate,
  CategoryDiscount
} from '../types/estimation-items';
import { estimationDataService } from './estimationDataService';
import { determineTVARate } from '../utils/tvaRules';
import coefficientsData from '../data/detailed-estimation-prices/coefficients.json';

interface Coefficients {
  tva: number;
  regional: Record<string, number>;
}

const coefficients = coefficientsData as Coefficients;

export class CalculationService {
  private static instance: CalculationService;

  private constructor() {}

  public static getInstance(): CalculationService {
    if (!CalculationService.instance) {
      CalculationService.instance = new CalculationService();
    }
    return CalculationService.instance;
  }

  public calculateCategoryTotal(
    categoryId: string,
    categoryData: CategoryData,
    buildingAge?: number,
    discountPercentage?: number
  ): CategoryTotal {
    const category = estimationDataService.getCategoryData(categoryId);

    if (!category) {
      return {
        categoryId,
        categoryTitle: categoryId,
        items: [],
        subtotal: 0
      };
    }

    const calculatedItems: CalculatedItem[] = [];

    Object.entries(categoryData).forEach(([itemKey, selection]) => {
      if (!selection.selected) return;

      const item = category.items[itemKey];
      if (!item) return;

      let quantity: number;
      let unitPrice: number;
      let totalPrice: number;

      if (item.customPricing) {
        quantity = 1;
        unitPrice = selection.value || 0;
        totalPrice = unitPrice;
      } else {
        quantity = selection.value || 1;
        unitPrice = item.price;
        totalPrice = quantity * unitPrice;
      }

      const tvaRate = determineTVARate(itemKey, buildingAge);
      const tvaAmount = totalPrice * (tvaRate / 100);
      const totalTTC = totalPrice + tvaAmount;

      calculatedItems.push({
        itemKey,
        name: item.name,
        quantity,
        unit: item.unit,
        unitPrice,
        totalPrice,
        category: categoryId,
        tvaRate,
        tvaAmount,
        totalTTC
      });
    });

    const subtotalBeforeDiscount = calculatedItems.reduce((sum, item) => sum + item.totalPrice, 0);

    let subtotal = subtotalBeforeDiscount;
    let discountAmount = 0;

    if (discountPercentage && discountPercentage > 0) {
      discountAmount = subtotalBeforeDiscount * (discountPercentage / 100);
      subtotal = subtotalBeforeDiscount - discountAmount;

      const discountRatio = subtotal / subtotalBeforeDiscount;

      calculatedItems.forEach(item => {
        item.totalPrice = item.totalPrice * discountRatio;
        item.tvaAmount = item.totalPrice * (item.tvaRate / 100);
        item.totalTTC = item.totalPrice + item.tvaAmount;
      });
    }

    return {
      categoryId,
      categoryTitle: category.title,
      items: calculatedItems,
      subtotal,
      discountPercentage,
      subtotalBeforeDiscount: discountPercentage ? subtotalBeforeDiscount : undefined,
      discountAmount: discountPercentage ? discountAmount : undefined
    };
  }

  private calculatePercentageItems(
    formData: DetailedEstimationData,
    baseAmountHT: number,
    buildingAge?: number
  ): CalculatedItem[] {
    const percentageItems: CalculatedItem[] = [];
    const categoryIds = estimationDataService.getCategoryIds();

    categoryIds.forEach(categoryId => {
      const categoryData = formData[categoryId as keyof DetailedEstimationData] as CategoryData;
      if (!categoryData || typeof categoryData !== 'object') return;

      const category = estimationDataService.getCategoryData(categoryId);
      if (!category) return;

      Object.entries(categoryData).forEach(([itemKey, selection]) => {
        if (!selection.selected) return;

        const item = category.items[itemKey];
        if (!item) return;

        if (item.unit === '%') {
          const percentageValue = selection.value || item.price;
          const totalPrice = baseAmountHT * (percentageValue / 100);

          const tvaRate = determineTVARate(itemKey, buildingAge);
          const tvaAmount = totalPrice * (tvaRate / 100);
          const totalTTC = totalPrice + tvaAmount;

          percentageItems.push({
            itemKey,
            name: item.name,
            quantity: percentageValue,
            unit: item.unit,
            unitPrice: baseAmountHT,
            totalPrice,
            category: categoryId,
            tvaRate,
            tvaAmount,
            totalTTC,
            isPercentageCalculated: true,
            baseAmountForPercentage: baseAmountHT
          });
        }
      });
    });

    return percentageItems;
  }

  public calculateEstimate(
    formData: DetailedEstimationData,
    departmentCode?: string,
    discounts?: CategoryDiscount[]
  ): EstimationResult {
    const categories: CategoryTotal[] = [];
    const categoryIds = estimationDataService.getCategoryIds();
    const buildingAge = formData.description?.buildingAge;

    const discountMap = new Map<string, number>();
    if (discounts) {
      discounts.forEach(d => discountMap.set(d.categoryId, d.percentage));
    }

    categoryIds.forEach(categoryId => {
      const categoryData = formData[categoryId as keyof DetailedEstimationData] as CategoryData;

      if (categoryData && typeof categoryData === 'object') {
        const discountPercentage = discountMap.get(categoryId);

        const category = estimationDataService.getCategoryData(categoryId);
        if (!category) return;

        const filteredCategoryData: CategoryData = {};
        Object.entries(categoryData).forEach(([itemKey, selection]) => {
          const item = category.items[itemKey];
          if (item && item.unit !== '%') {
            filteredCategoryData[itemKey] = selection;
          }
        });

        const categoryTotal = this.calculateCategoryTotal(categoryId, filteredCategoryData, buildingAge, discountPercentage);

        if (categoryTotal.items.length > 0) {
          categories.push(categoryTotal);
        }
      }
    });

    const totalBeforeDiscount = categories.reduce(
      (sum, cat) => sum + (cat.subtotalBeforeDiscount || cat.subtotal),
      0
    );
    const totalDiscountAmount = categories.reduce(
      (sum, cat) => sum + (cat.discountAmount || 0),
      0
    );

    let totalHTBeforePercentage = categories.reduce((sum, cat) => sum + cat.subtotal, 0);

    if (departmentCode && coefficients.regional[departmentCode]) {
      const regionalCoef = coefficients.regional[departmentCode];
      totalHTBeforePercentage = totalHTBeforePercentage * regionalCoef;

      categories.forEach(cat => {
        cat.subtotal = cat.subtotal * regionalCoef;
        if (cat.subtotalBeforeDiscount) {
          cat.subtotalBeforeDiscount = cat.subtotalBeforeDiscount * regionalCoef;
        }
        if (cat.discountAmount) {
          cat.discountAmount = cat.discountAmount * regionalCoef;
        }
        cat.items.forEach(item => {
          item.unitPrice = item.unitPrice * regionalCoef;
          item.totalPrice = item.totalPrice * regionalCoef;
          item.tvaAmount = item.tvaAmount * regionalCoef;
          item.totalTTC = item.totalTTC * regionalCoef;
        });
      });
    }

    const percentageItems = this.calculatePercentageItems(formData, totalHTBeforePercentage, buildingAge);

    if (percentageItems.length > 0) {
      const preparationCategory = categories.find(cat => cat.categoryId === 'preparation');
      if (preparationCategory) {
        preparationCategory.items.push(...percentageItems);
        preparationCategory.subtotal += percentageItems.reduce((sum, item) => sum + item.totalPrice, 0);
      } else {
        const category = estimationDataService.getCategoryData('preparation');
        if (category) {
          categories.push({
            categoryId: 'preparation',
            categoryTitle: category.title,
            items: percentageItems,
            subtotal: percentageItems.reduce((sum, item) => sum + item.totalPrice, 0)
          });
        }
      }
    }

    const totalHT = categories.reduce((sum, cat) => sum + cat.subtotal, 0);

    const tvaBreakdownMap = new Map<TVARate, { baseHT: number; tvaAmount: number }>();
    const allItems = categories.flatMap(cat => cat.items);

    allItems.forEach(item => {
      const existing = tvaBreakdownMap.get(item.tvaRate) || { baseHT: 0, tvaAmount: 0 };
      existing.baseHT += item.totalPrice;
      existing.tvaAmount += item.tvaAmount;
      tvaBreakdownMap.set(item.tvaRate, existing);
    });

    const tvaBreakdown: TVABreakdown[] = Array.from(tvaBreakdownMap.entries()).map(
      ([rate, values]) => ({
        rate,
        baseHT: Math.round(values.baseHT * 100) / 100,
        tvaAmount: Math.round(values.tvaAmount * 100) / 100,
        totalTTC: Math.round((values.baseHT + values.tvaAmount) * 100) / 100
      })
    ).sort((a, b) => a.rate - b.rate);

    const totalTVA = tvaBreakdown.reduce((sum, item) => sum + item.tvaAmount, 0);
    const totalTTC = totalHT + totalTVA;

    return {
      categories,
      totalHT: Math.round(totalHT * 100) / 100,
      totalTTC: Math.round(totalTTC * 100) / 100,
      tva: Math.round(totalTVA * 100) / 100,
      tvaBreakdown,
      totalBeforeDiscount: totalDiscountAmount > 0 ? Math.round(totalBeforeDiscount * 100) / 100 : undefined,
      totalDiscountAmount: totalDiscountAmount > 0 ? Math.round(totalDiscountAmount * 100) / 100 : undefined
    };
  }

  public formatPrice(price: number): string {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  }

  public getRegionalCoefficient(departmentCode: string): number {
    return coefficients.regional[departmentCode] || 1;
  }

  public getTVARate(): number {
    return coefficients.tva;
  }
}

export const calculationService = CalculationService.getInstance();
